import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeftIcon } from 'lucide-react';
import { useQueryState } from 'nuqs';

import { useLanguage } from '@/contexts/language-context';
import { useCurrentBusiness } from '@/hooks/use-current-business';
import { employeeQueryKey, getEmployee } from '@/lib/api/employees';
import {
  createPayrollPayment,
  listPayrollOwedForEmployee,
  payrollOwedDetailQueryKey,
  payrollOwedQueryKey,
  type CreatePayrollPaymentRequest,
} from '@/lib/api/payroll';

import { allVisitIds, sumSelectedPay } from '../helpers';
import { PaymentHistoryPanel } from './payment-history-panel';
import { SubmitBar } from './submit-bar';
import { VisitRow } from './visit-row';

export function PayrollEmployeeDetail() {
  const { t } = useLanguage();
  const { employeeId = '' } = useParams();
  const business = useCurrentBusiness();
  const businessId = business?.business_id;
  const queryClient = useQueryClient();

  const [from] = useQueryState('from');
  const [to] = useQueryState('to');
  const filters = { from: from ?? undefined, to: to ?? undefined };

  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [note, setNote] = useState('');
  const initializedRef = useRef(false);

  const employeeQuery = useQuery({
    queryKey: employeeQueryKey(businessId ?? '', employeeId),
    queryFn: () => getEmployee(businessId!, employeeId),
    enabled: Boolean(businessId && employeeId),
  });

  const visitsQuery = useQuery({
    queryKey: payrollOwedDetailQueryKey(businessId ?? '', employeeId, filters),
    queryFn: () => listPayrollOwedForEmployee(businessId!, employeeId, filters),
    enabled: Boolean(businessId && employeeId),
  });

  const visits = visitsQuery.data ?? [];

  useEffect(() => {
    if (visitsQuery.data && !initializedRef.current) {
      setSelected(allVisitIds(visitsQuery.data));
      initializedRef.current = true;
    }
  }, [visitsQuery.data]);

  const payMutation = useMutation({
    mutationFn: (body: CreatePayrollPaymentRequest) =>
      createPayrollPayment(businessId!, body),
    meta: { successMessage: t.payrollPaymentRecorded },
    onSuccess: () => {
      setSelected(new Set());
      setNote('');
    },
    onError: () => {
      // The global MutationCache (src/lib/query-client.ts) only invalidates
      // on success. A 409 here means these visits were paid from elsewhere
      // between load and submit, so the owed lists must be refreshed even
      // on failure — otherwise the owner could keep resubmitting stale ids.
      queryClient.invalidateQueries({
        queryKey: payrollOwedDetailQueryKey(businessId!, employeeId, filters),
      });
      queryClient.invalidateQueries({ queryKey: payrollOwedQueryKey(businessId!, filters) });
      setSelected(new Set());
    },
  });

  function toggleVisit(visitId: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(visitId)) next.delete(visitId);
      else next.add(visitId);
      return next;
    });
  }

  function handleSubmit() {
    if (selected.size === 0 || !businessId) return;
    payMutation.mutate({
      employee_id: employeeId,
      job_visit_employee_ids: Array.from(selected),
      note: note.trim() || undefined,
    });
  }

  const allSelected = visits.length > 0 && selected.size === visits.length;

  return (
    <div className="max-w-3xl mx-auto flex flex-col gap-5">
      <Link
        to="/payroll"
        className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 w-fit"
        data-testid="payroll-back"
      >
        <ChevronLeftIcon className="w-3.5 h-3.5" />
        {t.payrollBackToList}
      </Link>

      <h1 className="text-xl font-semibold leading-tight">
        {employeeQuery.data?.full_name ?? t.loading}
      </h1>

      {visitsQuery.isPending ? (
        <p className="text-sm text-muted-foreground py-10 text-center">{t.loading}</p>
      ) : visitsQuery.isError ? (
        <p className="text-sm text-destructive">{t.payrollLoadError}</p>
      ) : visits.length === 0 ? (
        <p className="text-sm text-muted-foreground py-10 text-center">
          {t.payrollNoOwedVisits}
        </p>
      ) : (
        <>
          <button
            type="button"
            onClick={() => setSelected(allSelected ? new Set() : allVisitIds(visits))}
            className="text-xs font-medium text-primary hover:underline w-fit"
            data-testid="payroll-select-toggle"
          >
            {allSelected ? t.payrollDeselectAll : t.payrollSelectAll}
          </button>

          <ul className="flex flex-col gap-2">
            {visits.map((visit) => (
              <VisitRow
                key={visit.job_visit_employee_id}
                visit={visit}
                selected={selected.has(visit.job_visit_employee_id)}
                onToggle={toggleVisit}
              />
            ))}
          </ul>

          <SubmitBar
            note={note}
            onNoteChange={setNote}
            selectedCount={selected.size}
            selectedTotal={sumSelectedPay(visits, selected)}
            onSubmit={handleSubmit}
            isSubmitting={payMutation.isPending}
          />
        </>
      )}

      {businessId && <PaymentHistoryPanel businessId={businessId} employeeId={employeeId} />}
    </div>
  );
}
